/**
 * useStudentValidateEmailState — les gestes de « Valider l'email », et son écriture.
 *
 * Les gestes de `ValidateEmailScreen.js` et de `ValidateEmailCard.js` :
 *  - la croix : le retour, ou l'accueil quand il n'y a rien derrière (`replace("menu")`) ;
 *  - « Modifier l'adresse » : l'écran de changement d'adresse (`edit-email`) ;
 *  - « Renvoyer l'email » : `emailResend` ; « Email renvoyé. » au succès, le refus du serveur sinon
 *    (l'ancien écran les disait dans une boîte, ils passent dans l'avis de la page) ;
 *  - « Actualiser » : la relecture du compte (`check`, de `useStudentValidateEmailData`) ;
 *  - et, sans geste, la fermeture dès que le compte est validé — à l'ouverture comme après une
 *    relecture.
 */
import { useNavigation } from "@react-navigation/native"
import { useCallback, useEffect, useReducer, useRef } from "react"

import { authService } from "../../../../../../data/services/authService"
import { type StudentValidateEmailNotice } from "../../../../../../design-system/pages/StudentValidateEmailPage/StudentValidateEmailPage"
import { useApiMutation } from "../../../hooks/useApiMutation"
import { type StudentValidateEmailViewModel } from "../../../../view-models/student/Account/studentValidateEmailViewModel"

type StackNavigation = {
    push: (route: string, params?: object) => void
    replace: (route: string, params?: object) => void
    goBack: () => void
    canGoBack: () => boolean
}

export const RESEND_DONE = "Email renvoyé."
export const RESEND_FAILED = "L'email n'a pas pu être renvoyé. Réessaie dans un instant."

export type ValidateEmailUiState = { notice: StudentValidateEmailNotice | null }

export type ValidateEmailUiAction = { type: "NOTICE"; notice: StudentValidateEmailNotice } | { type: "DISMISS_NOTICE" }

export function validateEmailUiReducer(
    state: ValidateEmailUiState,
    action: ValidateEmailUiAction
): ValidateEmailUiState {
    switch (action.type) {
        case "NOTICE":
            return { notice: action.notice }
        case "DISMISS_NOTICE":
            return state.notice == null ? state : { notice: null }
        default:
            return state
    }
}

export function useStudentValidateEmailState(vm: Pick<StudentValidateEmailViewModel, "verified">, check: () => void) {
    const navigation = useNavigation() as unknown as StackNavigation
    const [ui, dispatchUi] = useReducer(validateEmailUiReducer, { notice: null })

    const close = useCallback(() => {
        if (navigation.canGoBack()) navigation.goBack()
        else navigation.replace("menu")
    }, [navigation])

    // Une fois : la relecture qui suit la fermeture ne doit pas la rejouer sur l'écran d'en dessous.
    const closedOnVerifiedRef = useRef(false)
    useEffect(() => {
        if (!vm.verified || closedOnVerifiedRef.current) return
        closedOnVerifiedRef.current = true
        close()
    }, [vm.verified, close])

    const { start: startResend, requesting: resending } = useApiMutation({
        service: authService.emailResend,
        voice: "student",
        fallback: RESEND_FAILED,
        skipWhilePending: true,
        onSuccess: () => dispatchUi({ type: "NOTICE", notice: { message: RESEND_DONE, tone: "success" } }),
        onError: (message) => dispatchUi({ type: "NOTICE", notice: { message, tone: "danger" } }),
    })

    return {
        onClose: close,
        onEditEmail: () => navigation.push("edit-email"),
        onResend: () => {
            startResend()
        },
        resending,
        onCheck: check,
        notice: ui.notice,
        onDismissNotice: () => dispatchUi({ type: "DISMISS_NOTICE" }),
    }
}
